import { useLatest } from 'ahooks'
import { snakeCase } from 'lodash-es'
import UAParser from 'ua-parser-js'

import type { CommonParamsSchemaInput } from '@loc/server/interface/track/trackCommonParams.common'
import type { IEcommerce } from '.'

import { useLocale } from '@/hooks/routes'
import { sha256 } from '@/helpers/sha256'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useSiteSpecificContext } from '@/contexts/SiteSpecificContext'
import { getCookieUserGroup } from '@/hooks/userGroup/helpers/getCookieUserGroup'


import { useUrl } from '@/hooks/routes';
function getDeviceType(type?: string): 'Mobile' | 'Desktop' | 'Tablet' {
  if (['wearable', 'mobile'].includes(type || '')) {
    return 'Mobile'
  }
  if (['console', 'tablet', 'smarttv', 'embedded'].includes(type || '')) {
    return 'Tablet'
  }
  return 'Desktop'
}

export interface ITrackCommonParams {
  latestUserId?: string
  latestEmail?: string
}

export interface IUserTrackingInfo {
  user_id: CommonParamsSchemaInput['commUserId']
  user_group: CommonParamsSchemaInput['commUserGroup']
  user_status: CommonParamsSchemaInput['commUserStatus']
}

export interface CommonParamsResult {
  commonParams: CommonParamsSchemaInput
  ecommerce: IEcommerce
  gaCommonParams: {
    email_sha256: string
  } & IUserTrackingInfo
}

export function useTrackCommonParams() {
  const sessionInfo = useSessionInfo()
  const locale = useLocale()
  const url = useUrl()
  const { plan, user } = useSiteSpecificContext()

  const infoLatest = useLatest({
    sessionInfo,
    locale,
    url,
    plan,
    user,
  })

  const getCommonParams = async (
    props?: ITrackCommonParams,
    options?: { retryCount?: number },
  ): Promise<CommonParamsResult> => {
    const { latestUserId, latestEmail } = props || {}
    const { retryCount = 0 } = options || {}
    const { sessionInfo, locale, url, plan, user } = infoLatest.current
    const reqInfo = user?.reqInfo

    const { email, userId: sessionUserId } = sessionInfo
    const { isDataLoading } = plan
    const isLoading = isDataLoading || reqInfo?.isInitialLoading

    // 如果 plan 或 reqInfo 正在加载中且未超过最大重试次数，等待后重试
    if (isLoading && retryCount < 5) {
      await new Promise((resolve) => setTimeout(resolve, 500))
      return await getCommonParams(props, { retryCount: retryCount + 1 })
    }

    const clientUserId = sessionUserId || user.userId || ''
    const userGroup = getCookieUserGroup() || '101'

    const authInfo = {
      commUserId: latestUserId || clientUserId,
      commEmail: latestEmail || email || user.userEmail || '',
    }

    const urlInfo = {
      commCurrentUrl: url.href,
      commPathName: url.pathname,
      commHost: url.hostname,
    }

    // 设备信息获取
    const { browser, device, os } = new UAParser(
      navigator?.userAgent,
    ).getResult()
    const uaParserInfo = {
      commBrowser: browser.name || 'unknown',
      commDeviceType: getDeviceType(device.type),
      commOs: os.name || 'unknown',
      commDeviceId: sessionInfo.deviceId || '',
    }

    // 用户状态判断
    const getUserStatus = () => {
      if (!clientUserId) return 'anon'
      if (!plan.hasPlan) return 'free'
      if (!plan.isActiveSub) return 'expired'

      if (plan.creditsPlan && plan.isActiveSub) {
        const duration =
          plan.creditsPlan.productsSub.duration === 1 ? 'mo' : 'yr'
        const productName = plan.creditsPlan.productsSub.productName
        return `${snakeCase(productName)}_${plan.priceInfo.currencyInfo.symbol}${plan.priceInfo.totalPriceText}_${duration}`
      }

      return 'free'
    }

    const commUserStatus = getUserStatus()

    // 接口通用参数
    const commonParams: CommonParamsSchemaInput = {
      ...uaParserInfo,
      ...urlInfo,
      project: 'tad',
      commEmail: '',
      commUserId: authInfo.commUserId || sessionInfo.anonymousUserId || '',
      commUserStatus,
      commUserGroup: userGroup,
      commLanguage: locale,
      commCountry: reqInfo?.reqData?.country || 'unknown',
      commTimestamp: Date.now(),
    }

    // 各平台参数
    const userTrackingInfo: IUserTrackingInfo = {
      user_id: authInfo.commUserId,
      user_group: userGroup,
      user_status: commUserStatus,
    }

    // ga 通用参数
    const gaCommonParams: CommonParamsResult['gaCommonParams'] = {
      email_sha256: authInfo.commEmail
        ? await sha256(authInfo.commEmail.toLowerCase())
        : '',
      ...userTrackingInfo,
    }

    return {
      commonParams,
      gaCommonParams,
      ecommerce: plan.ecommerce,
    }
  }

  const getCommonParamsLatest = useLatest(getCommonParams)

  return getCommonParamsLatest
}
